import { supabase } from './supabase'

// ── Schedule cells ────────────────────────────────────────────────────────────

export async function loadSchedule() {
  // Paged read: PostgREST silently caps a response at 1000 rows (max-rows) with no error.
  // Once schedule_cells passed 1000 rows an unpaged select loaded an arbitrary 1000 and
  // edited cells "vanished" on reload. Ordered by id so pages are stable.
  const PAGE = 1000
  const data = []
  for (let from = 0; ; ) {
    const { data: page, error } = await supabase
      .from('schedule_cells')
      .select('*')
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1)
    if (error) { console.error('loadSchedule:', error); return {} }
    if (!page || page.length === 0) break
    data.push(...page)
    from += page.length   // advance by what came back, so a lower max-rows can't end the loop early
  }

  const cellData = {}
  data.forEach(row => {
    if (!cellData[row.job_id]) cellData[row.job_id] = {}
    cellData[row.job_id][row.date_key] = {
      count: row.count,
      foreman: row.foreman,
      task: row.task || '',
      workers: row.workers || [],
      // Phase 5 hybrid: ids carried alongside names (names remain source of truth)
      foreman_id: row.foreman_id ?? null,
      worker_ids: row.worker_ids || [],
      // Phase A inbound-first: default + per-worker cost codes for publish
      default_cost_code: row.default_cost_code || '',
      worker_cost_codes: row.worker_cost_codes || {},
    }
  })
  return cellData
}

// ── Phase 5 hybrid dual-write: resolve employee names → ids ───────────────────
// Names remain the source of truth; ids are written alongside so readers can
// migrate to ID-based lookup. See Phase 5 plan / api/schedule/today.js.
function isPlaceholderName(n) {
  return typeof n === 'string' && n.startsWith('[') && n.endsWith(']')
}

// name → employee, preferring is_foreman on duplicate names
// (mirrors the same-name disambiguation in api/schedule/today.js)
function bestEmpByName(roster) {
  const m = {}
  ;(roster || []).forEach(e => {
    if (!e || !e.name) return
    const key = e.name.toLowerCase()  // case-insensitive: "dane bilardo" → "Dane Bilardo"
    const ex = m[key]
    if (!ex || (e.isForeman && !ex.isForeman)) m[key] = e
  })
  return m
}

function cellIdFields(updates, roster) {
  const m = bestEmpByName(roster)
  const fm = updates.foreman || ''
  const fmEmp = fm && !isPlaceholderName(fm) ? m[fm.toLowerCase()] : null
  const ws = Array.isArray(updates.workers) ? updates.workers : []
  return {
    foreman_id: fmEmp ? fmEmp.id : null,
    // positional: aligned to workers[]; null element = placeholder / unmatched
    worker_ids: ws.map(n => {
      if (!n || isPlaceholderName(n)) return null
      const emp = m[n.toLowerCase()]
      return emp ? emp.id : null
    }),
  }
}

// Cost-code fields for a cell. Only included when the updates object carries
// them, so callers that don't touch cost codes leave the columns untouched on
// upsert (PostgREST only SETs provided columns).
function cellCostCodeFields(updates) {
  const out = {}
  if ('default_cost_code' in updates) out.default_cost_code = updates.default_cost_code || null
  if ('worker_cost_codes' in updates) out.worker_cost_codes = updates.worker_cost_codes || {}
  return out
}

export async function saveCell(jobId, dateKey, updates, userId, roster) {
  const { error } = await supabase
    .from('schedule_cells')
    .upsert({
      job_id: jobId,
      date_key: dateKey,
      foreman: updates.foreman || '',
      count: updates.count || 0,
      task: updates.task || '',
      workers: updates.workers || [],
      // Only write ids when a roster is supplied; omitting them leaves existing
      // values untouched on upsert (PostgREST only SETs provided columns).
      ...(roster ? cellIdFields(updates, roster) : {}),
      ...cellCostCodeFields(updates),
      updated_at: new Date().toISOString(),
      updated_by: userId,
    }, { onConflict: 'job_id,date_key' })
  if (error) console.error('saveCell:', error)
}

export async function saveCells(jobId, cellMap, userId, roster) {
  // cellMap: { dateKey: updates, ... }
  const rows = Object.entries(cellMap).map(([dateKey, updates]) => ({
    job_id: jobId,
    date_key: dateKey,
    foreman: updates.foreman || '',
    count: updates.count || 0,
    task: updates.task || '',
    workers: updates.workers || [],
    ...(roster ? cellIdFields(updates, roster) : {}),
    ...cellCostCodeFields(updates),
    updated_at: new Date().toISOString(),
    updated_by: userId,
  }))
  const { error } = await supabase
    .from('schedule_cells')
    .upsert(rows, { onConflict: 'job_id,date_key' })
  if (error) console.error('saveCells:', error)
}

// Cost codes for one job (for the schedule cost-code picker). Returns
// [{ code, description }] sorted by code, or [] on error / none.
export async function loadCostCodesForJob(jobId) {
  if (jobId == null) return []
  const { data, error } = await supabase
    .from('cost_codes')
    .select('cost_code_no, description')
    .eq('job_id', jobId)
    .order('cost_code_no', { ascending: true })
  if (error) { console.error('loadCostCodesForJob:', error); return [] }
  return (data || [])
    .filter(cc => cc.cost_code_no != null)
    .map(cc => ({ code: String(cc.cost_code_no), description: cc.description || '' }))
}

// Publish the schedule for a date (inbound-first trigger). Sends the logged-in
// user's Supabase access token; the server enforces owner/road_super. Returns
// the endpoint JSON ({ reports_created, reports_updated, emails_sent, ... }) or
// { error } on failure.
export async function publishSchedule(date) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/schedule/publish', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ date }),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'publish_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('publishSchedule:', e)
    return { error: 'network_error', detail: e.message }
  }
}

// Auto-send the daily schedule email (replaces the old mailto/Outlook flow).
// The caller builds the plain-text body; the server resolves recipients
// (all non-foreman app_users, publisher included) and sends from the Crew
// Dispatch address. Returns { sent, recipients } or { error } on failure.
export async function emailSchedule(subject, body) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/schedule/email-schedule', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ subject, body }),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'email_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('emailSchedule:', e)
    return { error: 'network_error', detail: e.message }
  }
}

// ── Crew texting (Twilio) ───────────────────────────────
// Server-side dispatch + confirmation status. Session-authed with the user's
// Supabase access token; the server enforces owner/road_super on dispatch.
// `date` must be "YYYY-MM-DD".

export async function dispatchTexts(date, jobId) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/text/dispatch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ date, job_id: jobId }),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'dispatch_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('dispatchTexts:', e)
    return { error: 'network_error', detail: e.message }
  }
}

export async function loadTextStatus(date, jobId) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const qs = new URLSearchParams({ date })
    if (jobId != null) qs.set('job_id', String(jobId))
    const res = await fetch(`/api/text/status?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'status_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('loadTextStatus:', e)
    return { error: 'network_error', detail: e.message }
  }
}


// Batch dispatch for a whole day (no job_id → every job with crew on that date).
// `preview: true` returns the plan (jobs, crew count, skips) WITHOUT sending.
// `includeDispatched: true` processes already-dispatched jobs, texting only crew who
// haven't been texted yet (safe retry / crew changed).
export async function dispatchAllTexts(date, { preview = false, includeDispatched = false } = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/text/dispatch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ date, preview, include_dispatched: includeDispatched }),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'dispatch_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('dispatchAllTexts:', e)
    return { error: 'network_error', detail: e.message }
  }
}


// ── "No work tomorrow" notices ───────────────────────────────────────────────
// The one-way notice for crew who are NOT on the board for a date. Owner/road_super only.
// Sent from the same modal as the dispatch-all send (src/components/DispatchSendModal.js).

// The candidate list + the server-built default message for `date` (YYYY-MM-DD).
export async function loadNoWorkCandidates(date) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch(`/api/text/no-work?${new URLSearchParams({ date }).toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'no_work_load_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('loadNoWorkCandidates:', e)
    return { error: 'network_error', detail: e.message }
  }
}

// `keys` are the opaque recipient keys from loadNoWorkCandidates — the server re-resolves
// them against live consent AND the live schedule, so a stale tab can neither text someone
// who has since opted out nor tell someone there is no work after they were scheduled.
// preview: true resolves + counts segments and sends nothing.
// resend: true texts people who were ALREADY told about this date. Off by default, and
// omitting it keeps today's behaviour — a repeat text is normally a bug, so it takes an
// explicit opt-in from the modal.
export async function sendNoWorkNotice(date, keys, body, { preview = false, resend = false } = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/text/no-work', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: preview ? 'preview' : 'send', date, keys, body, resend }),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'no_work_send_failed', detail: data.detail, skipped: data.skipped }
    return data
  } catch (e) {
    console.error('sendNoWorkNotice:', e)
    return { error: 'network_error', detail: e.message }
  }
}


// ── SMS opt-ins (consent roster + office override) ───────────────────────────

export async function loadOptIns() {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/text/optins', { headers: { Authorization: `Bearer ${token}` } })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'optins_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('loadOptIns:', e)
    return { error: 'network_error', detail: e.message }
  }
}

// action: 'record' (paper consent on file) | 'revoke'
export async function setOptIn(action, { name, phone }) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/text/optins', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, name, phone }),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'optin_update_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('setOptIn:', e)
    return { error: 'network_error', detail: e.message }
  }
}


// Send the opt-in confirmation text to everyone with live consent who was never sent one
// (sign-ups predating the confirmation feature, or whose send failed). Owner/road_super only.
export async function sendOptinConfirmations() {
  return setOptIn('send_confirmations', { name: '', phone: '' })
}


// ── Company-wide announcements (one-way broadcast texts) ─────────────────────

// The audience picker + recent send history. Owner/road_super only.
export async function loadAnnounceAudience() {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/text/broadcast', { headers: { Authorization: `Bearer ${token}` } })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'audience_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('loadAnnounceAudience:', e)
    return { error: 'network_error', detail: e.message }
  }
}

// action: 'preview' (resolve + count segments, sends nothing) | 'send'
// `keys` are the opaque recipient keys from loadAnnounceAudience — the server re-resolves
// them against live consent, so a stale tab can never text someone who has since opted out.
export async function sendAnnouncement(action, { body, keys }) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/text/broadcast', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, body, keys }),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'announce_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('sendAnnouncement:', e)
    return { error: 'network_error', detail: e.message }
  }
}

// ── Requests ──────────────────────────────────────────────────────────────────

export async function loadRequests() {
  const { data, error } = await supabase
    .from('requests')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) { console.error('loadRequests:', error); return [] }
  return data.map(row => ({
    id: row.id,
    jobId: row.job_id,
    jobName: row.job_name,
    dateKey: row.date_key,
    date: row.date_label,
    requestedBy: row.requested_by,
    role: row.role,
    workers: row.workers_requested,
    current: row.workers_current,
    daysOnsite: row.days_onsite || 1,
    includeSaturday: !!row.include_saturday,
    includeSunday: !!row.include_sunday,
    endDateKey: row.end_date_key || row.date_key,
    reason: row.reason,
    status: row.status,
    ts: new Date(row.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
  }))
}

export async function saveRequest(req, userId) {
  const { data, error } = await supabase
    .from('requests')
    .insert({
      job_id: req.jobId,
      job_name: req.jobName,
      date_key: req.dateKey,
      date_label: req.date,
      requested_by: req.requestedBy,
      role: req.role,
      workers_requested: req.workers,
      workers_current: req.current,
      days_onsite: req.daysOnsite || 1,
      include_saturday: !!req.includeSaturday,
      include_sunday: !!req.includeSunday,
      end_date_key: req.endDateKey || req.dateKey,
      reason: req.reason,
      status: 'pending',
      created_by: userId,
    })
    .select()
    .single()
  if (error) { console.error('saveRequest:', error); return null }
  return data.id
}

export async function updateRequestStatus(id, status) {
  const { error } = await supabase
    .from('requests')
    .update({ status })
    .eq('id', id)
  if (error) console.error('updateRequestStatus:', error)
}

// ── Time off ──────────────────────────────────────────────────────────────────

export async function loadTimeOff() {
  const { data, error } = await supabase
    .from('time_off')
    .select('*')
  if (error) { console.error('loadTimeOff:', error); return [] }
  return data
}

export async function saveTimeOffRecord(empId, empName, from, to, reason, userId) {
  const { data, error } = await supabase
    .from('time_off')
    .insert({
      employee_id: empId,
      employee_name: empName,
      from_date: from,
      to_date: to,
      reason: reason || '',
      created_by: userId,
    })
    .select()
    .single()
  if (error) { console.error('saveTimeOffRecord:', error); return null }
  return data.id
}

export async function deleteTimeOffRecord(dbId) {
  const { error } = await supabase
    .from('time_off')
    .delete()
    .eq('id', dbId)
  if (error) console.error('deleteTimeOffRecord:', error)
}

// ── Jobs ──────────────────────────────────────────────────────────────────────

export async function loadJobs() {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .order('sort_order', { ascending: true })
  if (error) { console.error('loadJobs:', error); return null }
  if (!data || data.length === 0) return null // null = use defaults from data.js
  return data.map(row => ({
    id: row.id,
    num: row.num,
    name: row.name,
    address: row.address || '',
    pm: row.pm || '',
    pmName: row.pm_name || '',
    pmEmail: row.pm_email || '',
    gc: row.gc || '',
    color: row.color || '#378ADD',
    active: row.active !== false,
  }))
}

export async function saveJob(job, sortOrder) {
  const { error } = await supabase
    .from('jobs')
    .upsert({
      id: job.id,
      num: job.num,
      name: job.name,
      address: job.address || '',
      pm: job.pm || '',
      gc: job.gc || '',
      color: job.color || '#378ADD',
      active: job.active !== false,
      sort_order: sortOrder,
    }, { onConflict: 'id' })
  if (error) console.error('saveJob:', error)
}

// NOTE: 'active' is deliberately NOT written here (2026-07-23). This bulk save
// runs on every job edit/reorder from whatever the browser has loaded, so a
// stale client could blast manual inactive flags back to active. Active changes
// go through setJobActive + saveActiveOverride (Admin toggle / job edit form).
export async function saveAllJobs(jobs) {
  const rows = jobs.map((j, i) => ({
    id: j.id,
    num: j.num,
    name: j.name,
    address: j.address || '',
    pm: j.pm || '',
    gc: j.gc || '',
    color: j.color || '#378ADD',
    sort_order: i,
  }))
  const { error } = await supabase
    .from('jobs')
    .upsert(rows, { onConflict: 'id' })
  if (error) console.error('saveAllJobs:', error)
}

export async function deleteJob(jobId) {
  const { error } = await supabase.from('jobs').delete().eq('id', jobId)
  if (error) console.error('deleteJob:', error)
}

// ── Employees ─────────────────────────────────────────────────────────────────

export async function saveEmployeeNotificationEmail(id, email, isForeman) {
  const { error } = await supabase
    .from('employees')
    .update({ email: email || '', is_foreman: isForeman || false })
    .eq('id', id)
  if (error) console.error('saveEmployeeNotificationEmail:', error)
}

export async function loadEmployees() {
  const { data, error } = await supabase
    .from('employees')
    .select('*')
    .order('id', { ascending: true })
  if (error) { console.error('loadEmployees:', error); return null }
  if (!data || data.length === 0) return null // null = use defaults from data.js
  return data.map(row => ({
    id: row.id,
    name: row.name,
    trade: row.trade || '',
    local: row.local || '',
    isForeman: row.is_foreman || false,
    phone: row.phone || '',
    cellPhone: row.cell_phone || '',
    email: row.email || '',
    foundation_email: row.foundation_email || '',
    city: row.city || '',
    state: row.state || '',
    dateLastWorked: row.date_last_worked || '',
    timeOff: [],
    active: row.active !== false,
    // Office-staff flag — separate from 'active'. See isSchedulable() in src/lib/roster.js.
    schedulable: row.schedulable !== false,
    // Same-name override (Admin → Employees → Display name). `name` above is ALREADY the
    // effective name (display_name || foundation_name, computed by the sync and by
    // setEmployeeDisplayName), so every reader keeps using `name`; these two are for the
    // Admin form only.
    displayName: row.display_name || '',
    foundationName: row.foundation_name || row.name,
  }))
}

// TODO: migrate to ID-based employee lookup in schedule_cells — see Phase 5 plan
// NOTE: 'active' is deliberately NOT written here (2026-07-23) — same reasoning
// as saveAllJobs. Active changes go through setEmployeeActive + saveActiveOverride.
// NOTE: 'schedulable' is deliberately NOT written here either — omitting it from this
// explicit column list is what makes the office-staff flag survive every Foundation sync.
// Do NOT add it. It is written only by setEmployeeSchedulable().
// NOTE: 'display_name' / 'foundation_name' are deliberately absent too. display_name is
// office-owned (written only by setEmployeeDisplayName) and foundation_name is
// Foundation-owned (written only by api/sync-foundation.js); 'name' below is the
// computed display_name || foundation_name the roster already carries. Do NOT add them.
export async function saveAllEmployees(employees) {
  const rows = employees.map(e => ({
    id: e.id,
    name: e.name,
    trade: e.trade || '',
    local: e.local || '',
    is_foreman: e.isForeman || false,
    phone: e.phone || '',
    cell_phone: e.cellPhone || '',
    foundation_email: e.foundation_email || '',
    city: e.city || '',
    state: e.state || '',
    // NOTE: 'email' (notification email) is intentionally excluded — never overwritten by sync
  }))
  // Upsert foundation data only — never touch 'email' (notification email)
  const { error } = await supabase
    .from('employees')
    .upsert(rows, { onConflict: 'id' })
  if (error) { console.error('saveAllEmployees:', error); return }

  // NOTE: 'email' and 'is_foreman' are managed separately via saveEmployeeNotificationEmail
  // They are never touched here to prevent overwrites
}

export async function deleteEmployee(empId) {
  const { error } = await supabase.from('employees').delete().eq('id', empId)
  if (error) console.error('deleteEmployee:', error)
}

// ── Display name (same-name employees) ───────────────────────────────────────
// `employees.name` is a JOIN KEY, not a label. schedule_cells.workers[] / foreman /
// worker_cost_codes, daily_field_reports.foreman_name + attendance[].employee_name,
// app_users.name (office phone lookups for escalations + announcements), Sarah's
// worker matcher and the payroll CSV's name fallback all follow the CURRENT name, and
// a rename orphans whatever is keyed on the OLD one. So a display name may only be set
// on the duplicate with NO name-keyed footprint — nameFootprint() is what the Admin
// guard measures that with, and renameInFutureSchedule() re-keys the one thing that
// SHOULD follow the person forward (upcoming schedule cells). History keeps the name it
// was dispatched under.

// Session-authed GET against api/employees — the report/dispatch half of the footprint
// lives in tables the browser cannot read (RLS on, zero policies), so it is counted
// server-side. Returns the endpoint JSON or { error }.
async function employeesGet(action, params = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token || null
  if (!token) return { error: 'not_signed_in' }
  const qs = new URLSearchParams({ action })
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') qs.set(k, String(v))
  try {
    const res = await fetch('/api/employees?' + qs.toString(), { headers: { Authorization: `Bearer ${token}` } })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'request_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('employeesGet:', action, e)
    return { error: 'network_error', detail: e.message }
  }
}

const nameKeyOf = (n) => String(n == null ? '' : n).trim().toLowerCase()

// schedule_cells.date_key is toDateString() text ("Mon May 11 2026"), NOT YYYY-MM-DD,
// so "future" cannot be a string comparison in the query — parse and compare locally.
function cellDate(dateKey) {
  const d = new Date(String(dateKey || ''))
  return isNaN(d) ? null : d
}
function startOfToday() {
  const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), t.getDate())
}

// What is keyed on this employee's CURRENT name — counted TWICE per category: rows that
// match the NAME, and rows anchored to this employee's ID (worker_ids / foreman_id,
// attendance employee_id, dispatch recipient employee_id, app_users.employee_id).
//
// ⚠ THE ID COUNT IS WHAT DECIDES. In a duplicate-name pair both twins match the same name
// rows by definition, so a name-only count blocks BOTH — including the new employee the
// rule says is the safe one to rename. On staging #719 and #730 each see 7 cells / 6
// reports by name; by id #719 owns all 7 cells and #730 owns none. The name counts are
// still returned so the UI can say whose rows they are.
//
// Read-only. Counts the browser can take itself (schedule cells, app_users) are taken
// here; reports and dispatch rows come from api/employees. If that call FAILS the report
// counts come back null and `reportsUnknown` is true — the caller must treat unknown as
// BLOCKED, never as zero.
export async function nameFootprint(empId, name) {
  const key = nameKeyOf(name)
  const out = {
    // by NAME
    cells: 0, foremanCells: 0, costCodeKeys: 0, reportsAsForeman: null, reportsInCrew: null, futureDispatch: null, login: 0,
    // by EMPLOYEE ID (what decides)
    idCells: 0, idForemanCells: 0, idReportsInCrew: null, idFutureDispatch: null, idLogin: 0,
    reportsUnknown: false,
  }
  if (!key) return out
  const id = empId != null && empId !== '' ? Number(empId) : null

  const SEL = 'id, foreman, foreman_id, workers, worker_ids, worker_cost_codes'
  const [{ data: byWorkers }, { data: byForeman }, { data: byIds }, { data: byFmId }] = await Promise.all([
    supabase.from('schedule_cells').select(SEL).contains('workers', JSON.stringify([name])),
    supabase.from('schedule_cells').select(SEL).eq('foreman', name),
    id != null ? supabase.from('schedule_cells').select(SEL).contains('worker_ids', [id]) : Promise.resolve({ data: [] }),
    id != null ? supabase.from('schedule_cells').select(SEL).eq('foreman_id', id) : Promise.resolve({ data: [] }),
  ])
  const cells = new Map()
  ;[...(byWorkers || []), ...(byForeman || []), ...(byIds || []), ...(byFmId || [])].forEach(c => cells.set(c.id, c))
  for (const c of cells.values()) {
    const workers = Array.isArray(c.workers) ? c.workers : []
    const ids = Array.isArray(c.worker_ids) ? c.worker_ids : []
    const inCrew = workers.some(w => nameKeyOf(w) === key)
    const isFm = nameKeyOf(c.foreman) === key
    if (inCrew || isFm) out.cells++
    if (isFm) out.foremanCells++
    const cc = c.worker_cost_codes && typeof c.worker_cost_codes === 'object' ? c.worker_cost_codes : {}
    if (Object.keys(cc).some(k => nameKeyOf(k) === key)) out.costCodeKeys++
    // id-anchored: positional worker_ids slot or foreman_id (compared as numbers — both
    // are integer columns)
    const idInCrew = id != null && ids.some(v => v != null && Number(v) === id)
    const idIsFm = id != null && c.foreman_id != null && Number(c.foreman_id) === id
    if (idInCrew || idIsFm) out.idCells++
    if (idIsFm) out.idForemanCells++
  }

  const { data: users } = await supabase.from('app_users').select('id, name, employee_id')
  out.login = (users || []).filter(u => nameKeyOf(u.name) === key).length
  out.idLogin = id != null ? (users || []).filter(u => u.employee_id != null && Number(u.employee_id) === id).length : 0

  const r = await employeesGet('name-footprint', { name, employee_id: id })
  if (r.error) {
    console.error('nameFootprint (reports):', r.error, r.detail || '')
    out.reportsUnknown = true
  } else {
    out.reportsAsForeman = r.reports_as_foreman || 0
    out.reportsInCrew = r.reports_in_crew || 0
    out.futureDispatch = r.future_dispatch_recipients || 0
    out.idReportsInCrew = r.id_reports_in_crew || 0
    out.idFutureDispatch = r.id_future_dispatch_recipients || 0
  }
  return out
}

// Set (or clear, with '') the office override. `name` is recomputed the same way the
// sync computes it, so the roster and every name-keyed reader see the new spelling
// immediately rather than after the next sync.
export async function setEmployeeDisplayName(id, displayName, foundationName) {
  const v = (displayName || '').trim()
  const name = v || foundationName
  const { error } = await supabase.from('employees').update({ display_name: v || null, name }).eq('id', id)
  if (error) { console.error('setEmployeeDisplayName:', error); return { error: error.message } }
  return { ok: true, name }
}

// After a rename: carry the NEW name onto the employee's upcoming schedule cells —
// workers[i] where worker_ids[i] is this employee, foreman where foreman_id is, and the
// name-keyed worker_cost_codes entry. Cells before today are untouched (history keeps the
// name it was dispatched under). Returns { cells, warnings }.
export async function renameInFutureSchedule(empId, oldName, newName) {
  const warnings = []
  const oldKey = nameKeyOf(oldName)
  if (!empId || !newName || oldKey === nameKeyOf(newName)) return { cells: 0, warnings }
  const SEL = 'id, date_key, foreman, foreman_id, workers, worker_ids, worker_cost_codes'
  const [{ data: byIds, error: e1 }, { data: byFm, error: e2 }] = await Promise.all([
    supabase.from('schedule_cells').select(SEL).contains('worker_ids', [empId]),
    supabase.from('schedule_cells').select(SEL).eq('foreman_id', empId),
  ])
  if (e1 || e2) {
    console.error('renameInFutureSchedule:', e1 || e2)
    warnings.push('Could not read the schedule — upcoming cells were NOT updated.')
    return { cells: 0, warnings }
  }
  const today = startOfToday()
  const rows = new Map()
  ;[...(byIds || []), ...(byFm || [])].forEach(c => rows.set(c.id, c))
  let updated = 0
  for (const c of rows.values()) {
    const d = cellDate(c.date_key)
    if (!d || d < today) continue
    const workers = Array.isArray(c.workers) ? c.workers : []
    const ids = Array.isArray(c.worker_ids) ? c.worker_ids : []
    const nextWorkers = workers.map((w, i) => (ids[i] === empId ? newName : w))
    const nextForeman = c.foreman_id === empId ? newName : c.foreman
    const cc = c.worker_cost_codes && typeof c.worker_cost_codes === 'object' ? { ...c.worker_cost_codes } : {}
    let ccChanged = false
    for (const k of Object.keys(cc)) {
      if (nameKeyOf(k) === oldKey) { cc[newName] = cc[k]; delete cc[k]; ccChanged = true }
    }
    const changed = ccChanged || nextForeman !== c.foreman || nextWorkers.some((w, i) => w !== workers[i])
    if (!changed) continue
    const patch = { workers: nextWorkers, foreman: nextForeman }
    if (ccChanged) patch.worker_cost_codes = cc
    const { error } = await supabase.from('schedule_cells').update(patch).eq('id', c.id)
    if (error) { console.error('renameInFutureSchedule update:', c.id, error); warnings.push(`Cell ${c.date_key} could not be updated.`); continue }
    updated++
  }

  // Counted, not rewritten: upcoming cells that carry the OLD name by string with no id
  // in the slot (pre-Phase-5 cells / free-text adds) — nothing proves they are this person.
  const { data: strOnly } = await supabase.from('schedule_cells').select('id, date_key, workers, worker_ids, foreman, foreman_id').contains('workers', JSON.stringify([oldName]))
  const orphan = (strOnly || []).filter(c => {
    const d = cellDate(c.date_key); if (!d || d < today) return false
    const ids = Array.isArray(c.worker_ids) ? c.worker_ids : []
    return (c.workers || []).some((w, i) => nameKeyOf(w) === oldKey && ids[i] !== empId)
  }).length
  if (orphan) warnings.push(`${orphan} upcoming schedule cell(s) list "${oldName}" with no employee id in the slot and were left alone — check the board.`)

  // Future-dated rows in other name-keyed tables (crew confirmations / pending texts)
  // are counted and reported, never rewritten.
  const r = await employeesGet('name-footprint', { name: oldName })
  if (!r.error && r.future_dispatch_recipients > 0) {
    warnings.push(`${r.future_dispatch_recipients} upcoming dispatch-text recipient row(s) still carry "${oldName}" (confirmations keep working — they bind by phone).`)
  }
  return { cells: updated, warnings }
}

// ── Active overrides ──────────────────────────────────────────────────────────

export async function saveActiveOverride(entityType, entityId, active) {
  const { error } = await supabase
    .from('active_overrides')
    .upsert({ entity_type: entityType, entity_id: entityId, active, updated_at: new Date().toISOString() },
      { onConflict: 'entity_type,entity_id' })
  if (error) console.error('saveActiveOverride:', error)
}

// Targeted active-flag writes. Callers always pair these with saveActiveOverride
// so the choice is durable across Foundation syncs (the override layer wins).
export async function setJobActive(jobId, active) {
  const { error } = await supabase.from('jobs').update({ active }).eq('id', jobId)
  if (error) console.error('setJobActive:', error)
}

export async function setEmployeeActive(empId, active) {
  const { error } = await supabase.from('employees').update({ active }).eq('id', empId)
  if (error) console.error('setEmployeeActive:', error)
}

// Office-staff flag. Sync-durable on its own (sync-foundation.js and saveAllEmployees
// both upsert explicit column lists that omit it), so unlike 'active' this needs no
// active_overrides row.
export async function setEmployeeSchedulable(empId, schedulable) {
  const { error } = await supabase.from('employees').update({ schedulable }).eq('id', empId)
  if (error) console.error('setEmployeeSchedulable:', error)
}

// ── Sync log ──────────────────────────────────────────────────────────────────

export async function loadSyncLog() {
  const { data, error } = await supabase.from('sync_log').select('*').eq('id', 1).single()
  if (error) return null
  return data
}

// ── Trigger sync from browser ─────────────────────────────────────────────────

export async function triggerFoundationSync(anonKey) {
  const response = await fetch('/api/sync-foundation', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${anonKey}`,
    },
  })
  const data = await response.json()
  return data
}

// ── Job order ─────────────────────────────────────────────────────────────────
// Stored in job_order table as a simple JSON array of job IDs

export async function loadJobOrder() {
  try {
    // Delete any duplicate rows first, keep only id=1
    await supabase.from('job_order').delete().neq('id', 1)
    const { data, error } = await supabase
      .from('job_order')
      .select('order_ids')
      .eq('id', 1)
      .maybeSingle()
    if (error) { console.error('loadJobOrder error:', JSON.stringify(error)); return null }
    const ids = data?.order_ids
    if (!ids || !Array.isArray(ids) || ids.length === 0) return null
    return ids
  } catch(e) {
    console.error('loadJobOrder exception:', e)
    return null
  }
}

export async function saveJobOrder(orderIds) {
  try {
    if (!orderIds || !Array.isArray(orderIds)) return
    // Delete all rows then insert fresh to avoid any constraint issues
    await supabase.from('job_order').delete().neq('id', 0)
    const { error } = await supabase
      .from('job_order')
      .insert({ id: 1, order_ids: orderIds, updated_at: new Date().toISOString() })
    if (error) console.error('saveJobOrder error:', JSON.stringify(error))
  } catch(e) {
    console.error('saveJobOrder exception:', e)
  }
}

// ── User roles ────────────────────────────────────────────────────────────────

export async function loadUserRole(userId) {
  const { data, error } = await supabase
    .from('app_users')
    .select('role, name, email')
    .eq('id', userId)
    .single()
  if (error) return null
  return data
}

export async function loadAllUsers() {
  console.log('loadAllUsers: fetching...')
  const { data, error } = await supabase
    .from('app_users')
    .select('id, email, name, role, employee_id')
    .order('role', { ascending: true })
  console.log('loadAllUsers result:', { data, error })
  if (error) { console.error('loadAllUsers error:', JSON.stringify(error)); return [] }
  return data || []
}

export async function updateUserRole(id, role) {
  if (!['owner','road_super','admin','pm','foreman'].includes(role)) return
  const { error } = await supabase
    .from('app_users')
    .update({ role })
    .eq('id', id)
  if (error) console.error('updateUserRole:', error)
}

export async function deleteUser(id) {
  const { error } = await supabase
    .from('app_users')
    .delete()
    .eq('id', id)
  if (error) console.error('deleteUser:', error)
}
// ── Employee certifications (api/certs) ──────────────────────────────────────
// Session-authed; the server owns every role decision. GET actions take query
// params, POST actions a JSON body. Both return the endpoint JSON or { error }.
async function certsToken() {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || null
}

export async function certsGet(action, params = {}) {
  const token = await certsToken()
  if (!token) return { error: 'not_signed_in' }
  const qs = new URLSearchParams({ action })
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') qs.set(k, String(v))
  try {
    const res = await fetch('/api/certs?' + qs.toString(), { headers: { Authorization: `Bearer ${token}` } })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'request_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('certsGet:', action, e)
    return { error: 'network_error', detail: e.message }
  }
}

export async function certsPost(action, body = {}) {
  const token = await certsToken()
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/certs?action=' + encodeURIComponent(action), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'request_failed', detail: data.detail }
    return data
  } catch (e) {
    console.error('certsPost:', action, e)
    return { error: 'network_error', detail: e.message }
  }
}

// Upload a certificate file STRAIGHT to the private bucket. The API mints a
// signed upload URL after checking the role + extension/mime/size (a Vercel
// function body is capped at ~4.5 MB, so the file never goes through the API);
// the browser PUTs the file to storage; the returned descriptor is then passed
// as `file` to certsPost('add' | 'replace'), which verifies the object landed.
const CERT_EXT_MIME = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', heic: 'image/heic' }
export async function uploadCertFile(employeeId, file) {
  if (!file) return { error: 'no_file' }
  const ext = (/.([A-Za-z0-9]+)$/.exec(file.name || '') || [])[1]?.toLowerCase() || ''
  // Browsers often report an empty type for HEIC — fall back to the extension.
  const mime = file.type || CERT_EXT_MIME[ext] || ''
  const minted = await certsPost('upload-url', { employee_id: employeeId, file_name: file.name, file_size: file.size, mime_type: mime })
  if (minted.error) return minted
  try {
    const { error } = await supabase.storage.from('employee-certs')
      .uploadToSignedUrl(minted.path, minted.token, file, { contentType: mime || undefined, upsert: false })
    if (error) return { error: 'upload_failed', detail: error.message }
  } catch (e) {
    return { error: 'upload_failed', detail: e.message }
  }
  return { path: minted.path, name: file.name, size: file.size, mime }
}

// ── New hire intake (api/new-hires) ──────────────────────────────────────────
// Session-authed; the server owns every role decision (foremen enter, the office
// reviews / sets the rate / approves). Same shape as certsGet / certsPost.
export async function newHiresGet(action, params = {}) {
  const token = await certsToken()
  if (!token) return { error: 'not_signed_in' }
  const qs = new URLSearchParams({ action })
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') qs.set(k, String(v))
  try {
    const res = await fetch('/api/new-hires?' + qs.toString(), { headers: { Authorization: `Bearer ${token}` } })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'request_failed', detail: data.detail, errors: data.errors }
    return data
  } catch (e) {
    console.error('newHiresGet:', action, e)
    return { error: 'network_error', detail: e.message }
  }
}

// Download ONE signed packet document (owner / Carla). Streams straight from DocuSign
// through the API to the browser; nothing is saved anywhere on the way.
export async function downloadNewHirePacketDoc(intakeId, documentId) {
  const token = await certsToken()
  if (!token) return { error: 'not_signed_in' }
  try {
    const qs = new URLSearchParams({ action: 'packet-doc', intake_id: String(intakeId), document_id: String(documentId) })
    const res = await fetch('/api/new-hires?' + qs.toString(), { headers: { Authorization: `Bearer ${token}` } })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      return { error: data.error || 'request_failed', detail: data.detail }
    }
    const m = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') || '')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = m ? m[1] : 'document.pdf'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10000)
    return { ok: true }
  } catch (e) {
    console.error('downloadNewHirePacketDoc:', e)
    return { error: 'network_error', detail: e.message }
  }
}

export async function newHiresPost(action, body = {}) {
  const token = await certsToken()
  if (!token) return { error: 'not_signed_in' }
  try {
    const res = await fetch('/api/new-hires?action=' + encodeURIComponent(action), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    const data = await res.json()
    if (!res.ok) return { error: data.error || 'request_failed', detail: data.detail, errors: data.errors, status: data.status }
    return data
  } catch (e) {
    console.error('newHiresPost:', action, e)
    return { error: 'network_error', detail: e.message }
  }
}

// New hire photos (2026-10-17): ID documents the new hire chose for the I-9, and
// certification cards. Same signed-upload pattern as uploadCertFile — the file goes
// STRAIGHT from the phone to the private `new-hire-docs` bucket (temp folder); the
// returned descriptor rides in the create body and the API checks + moves it.
export function uploadNewHireFile(kind, file) {
  return uploadNewHireVia((b) => newHiresPost('upload-url', b), kind, file)
}

// `mint` gets { kind, file_name, file_size, mime_type } and returns { path, token } or { error }.
async function uploadNewHireVia(mint, kind, file) {
  if (!file) return { error: 'no_file' }
  const ext = (/\.([A-Za-z0-9]+)$/.exec(file.name || '') || [])[1]?.toLowerCase() || ''
  const mime = file.type || CERT_EXT_MIME[ext] || ''
  // A phone camera photo can arrive with no extension in its name - give it one.
  const name = ext ? file.name : `photo.${mime === 'image/png' ? 'png' : mime === 'application/pdf' ? 'pdf' : 'jpg'}`
  const minted = await mint({ kind, file_name: name, file_size: file.size, mime_type: mime })
  if (minted.error) return minted
  try {
    const { error } = await supabase.storage.from('new-hire-docs')
      .uploadToSignedUrl(minted.path, minted.token, file, { contentType: mime || undefined, upsert: false })
    if (error) return { error: 'upload_failed', detail: error.message }
  } catch (e) {
    return { error: 'upload_failed', detail: e.message }
  }
  return { path: minted.path, name, size: file.size, mime }
}

export function discardNewHireUpload(path) {
  if (!path) return Promise.resolve({ ok: true })
  return newHiresPost('discard-upload', { path })
}

// ── The new hire's own page (/new-hire/start?t=) — NO session. The link token is
// the only key; the server decides everything (2026-10-24).
async function newHirePublicCall(method, action, token, lang, data = {}) {
  try {
    const qs = new URLSearchParams({ action })
    let init = { method }
    if (method === 'GET') {
      for (const [k, v] of Object.entries({ t: token, lang, ...data })) if (v != null && v !== '') qs.set(k, String(v))
    } else {
      init = { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...data, t: token, lang }) }
    }
    const res = await fetch('/api/new-hires?' + qs.toString(), init)
    const out = await res.json()
    if (!res.ok) return { error: out.error || 'request_failed', detail: out.detail, errors: out.errors, status: res.status }
    return out
  } catch (e) {
    console.error('newHirePublic:', action, e)
    return { error: 'network_error', detail: e.message }
  }
}

// The same shape the staff form uses, pointed at the public routes.
export function newHirePublicApi(token, lang) {
  return {
    get: (action, params) => newHirePublicCall('GET', 'public-' + action, token, lang, params),
    post: (action, body) => newHirePublicCall('POST', 'public-' + action, token, lang, body),
    upload: (kind, file) => uploadNewHireVia((b) => newHirePublicCall('POST', 'public-upload-url', token, lang, b), kind, file),
    discard: (path) => path ? newHirePublicCall('POST', 'public-discard-upload', token, lang, { path }) : Promise.resolve({ ok: true }),
  }
}
